/**
 * useAdminTeamState - Custom hook for Admin Team module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialNewTeamMember = { email: '', password: '', name: '', role: 'admin' };
const initialPasswordData = { current_password: '', new_password: '', confirm_password: '' };
const initialResetPasswordData = { email: '', new_password: '', admin_password: '' };

export const useAdminTeamState = ({ setError, setSuccess }) => {
  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  
  // Modal states
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  
  // Form states
  const [newTeamMember, setNewTeamMember] = useState({ ...initialNewTeamMember });
  const [passwordData, setPasswordData] = useState({ ...initialPasswordData });
  const [resetPasswordData, setResetPasswordData] = useState({ ...initialResetPasswordData });

  // Fetch team members
  const fetchTeamMembers = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/team`, {
        headers: getAuthHeaders()
      });
      setTeamMembers(response.data || []);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to load team members');
    } finally {
      setLoadingTeam(false);
    }
  }, [setError]);

  // Add new team member
  const handleAddTeamMember = useCallback(async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/team`, newTeamMember, {
        headers: getAuthHeaders()
      });
      setSuccess('Team member added successfully');
      setShowAddTeamMember(false);
      setNewTeamMember({ ...initialNewTeamMember });
      fetchTeamMembers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add team member');
    }
  }, [newTeamMember, fetchTeamMembers, setError, setSuccess]);

  // Remove team member
  const handleRemoveTeamMember = useCallback(async (adminId, email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from the admin team?`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/admin/team/${adminId}`, {
        headers: getAuthHeaders()
      });
      setSuccess('Team member removed successfully');
      fetchTeamMembers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove team member');
    }
  }, [fetchTeamMembers, setError, setSuccess]);

  // Change own password
  const handleChangePassword = useCallback(async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: getAuthHeaders()
      });
      setSuccess('Password changed successfully');
      setShowChangePassword(false);
      setPasswordData({ ...initialPasswordData });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    }
  }, [passwordData, setError, setSuccess]);

  // Reset another admin's password
  const handleResetPassword = useCallback(async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/reset-password`, resetPasswordData, {
        headers: getAuthHeaders()
      });
      setSuccess(`Password reset successfully for ${resetPasswordData.email}`);
      setShowResetPassword(false);
      setResetPasswordData({ ...initialResetPasswordData });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    }
  }, [resetPasswordData, setError, setSuccess]);

  return {
    // State
    teamMembers,
    loadingTeam,
    showAddTeamMember,
    showChangePassword,
    showResetPassword,
    newTeamMember,
    passwordData,
    resetPasswordData,

    // Setters
    setTeamMembers,
    setShowAddTeamMember,
    setShowChangePassword,
    setShowResetPassword,
    setNewTeamMember,
    setPasswordData,
    setResetPasswordData,

    // Actions
    fetchTeamMembers,
    handleAddTeamMember,
    handleRemoveTeamMember,
    handleChangePassword,
    handleResetPassword,
  };
};

export default useAdminTeamState;
